<template>
	<mobile-landing-page v-if="isInMobile && !getMobileAppState && !authorizeSession" :data-theme="[getAppTheme]"/>
	<div
    v-else-if="showApp"
    id="app-content"
    :class="{ 'apply-transparency': getApplyTransparencyToApp, 'mobile-app': getMobileAppState }"
    :data-theme="[getAppTheme]">
		<div
		v-if="getMobileAppState"
		class="status-bar-background-layer"
		:class="{ 'mobile-status-bar-android': getDeviceInUse === 'android', 'mobile-status-bar-ios': getDeviceInUse === 'ios' }"
		:style="{ 'opacity': 0 }"/>
		<!-- Mobile Base Structure -->
		<app-mobile-base
		v-if="getMobileAppState && notDesignShowroom"
		:nonAuthenticatedArea="nonAuthenticatedArea"
		:showLoginSpinner="showLoginSpinner"
		@updateLoginLoader="updateLoginLoaderHandler"
		@reRenderSvg="reRenderSvgFlag = !reRenderSvgFlag"
		@applyLoginLoaderExitAnimation="applyLoginLoaderExitAnimation = true"/>
		<!-- Web Base Structure -->
		<app-web-base
		v-else
		:nonAuthenticatedArea="nonAuthenticatedArea"
		:applyLighterBackground="applyLighterBackground"
		:showLoginSpinner="showLoginSpinner"
		@updateLoginLoader="updateLoginLoaderHandler"
		@applyLoginLoaderExitAnimation="applyLoginLoaderExitAnimation = true"/>
		<!-- Layer for Camera Stuff -->
		<div id="layer-for-camera" class="dont-apply-transparency"></div>
		<!-- *********************************************
		*                                                *
		*                 Teleports Layers               *
		*                                                *
		********************************************** -->
		<!-- These layers are all above our base, and have specific z-index, so we will have layers like shrek or onions -->
		<!-- Viewer Teleport, NOTE z-index is 125, mixin style -> viewerOverlayTeleportStyles -->
		<div id="pdf-vuer-overlay-teleport"/>
		<!-- Side Layer Overlay Teleport, NOTE z-index is 240, mixin style -> sideLayer2OverlayTeleportStyles -->
		<div id="search-mobile-layer-overlay-teleport"/>
		<!-- Side Layer Overlay Teleport, NOTE z-index is 250, mixin style -> sideLayer2OverlayTeleportStyles -->
		<div id="side-layer-overlay-teleport"/>
		<!-- Side Layer Overlay Teleport, NOTE z-index is 315, mixin style -> secondSidelayerOverlayTeleportStyles -->
		<div id="second-side-layer-overlay-teleport"/>
		<!-- Contacts Overlay Teleport, NOTE z-index is 325, mixin style -> contactsOverlayTeleportStyles -->
		<div id="contacts-overlay-teleport" />
		<!-- Multiple Contacts Overlay Teleport, NOTE z-index is 335, mixin style -> contactsMultipleOverlayTeleportStyles -->
		<div id="multiple-contacts-overlay-teleport" />
		<!-- More Options Mobile Layer, NOTE z-index is 340, mixin style -> moreOptionsMobilelayerOverlayTeleportStyles -->
		<div id="more-options-mobile-layer-overlay-teleport"/>
		<!-- Modals Overlay Teleport, NOTE z-index is 350, mixin style -> modalsOverlayTeleportStyles -->
		<div id="modals-centered-overlay-teleport"/>
		<!-- Modals Overlay Teleport, NOTE z-index is 380, mixin style -> modalsOverlayTeleportStyles -->
		<div id="second-modals-centered-overlay-teleport"/>
		<!-- Toasts Overlay Teleport, NOTE z-index is 450, mixin style -> toatsOverlayTeleportStyles -->
		<div id="toasts-overlay-teleport" class="dont-apply-transparency"/>
		<!-- Modals Over Camera Layer Overlay Teleport, NOTE z-index is 450, mixin style -> toatsOverlayTeleportStyles -->
		<div id="modals-centered-over-camera-teleport" class="dont-apply-transparency"/>
		<!-- Spinner Overlay Teleport, NOTE z-index is 500, mixin style -> spinnerOverlayTeleportStyles -->
		<div id="spinner-overlay-teleport"/>
		<!-- Login Loader -->
		<loaders-login
		v-if="showLoginSpinner"
		:applyLoginLoaderExitAnimation="applyLoginLoaderExitAnimation"
		:triggerLoginSimpleExit="triggerLoginSimpleExit"
		:reRenderSvg="reRenderSvgFlag"
		@closeLoginLoader="[showLoginSpinner = false, triggerLoginSimpleExit = false]"/>
        <!-- Rooted Device Warning Modal -->
        <modal-generic
        v-if="showRootedWarningModal"
        negative
        showOnParent
        leftButtonIcon="check-line"
        :title="modalTitle"
        :text="t('rootedWarningModal.text')"
        :leftButtonText="t('rootedWarningModal.leftButtonText')"
        @hideMe="showRootedWarningModal = false"/>
    </div>
</template>

<script setup>
import MobileLandingPage from '@/components/generic/MobileLandingPage'
import AppMobileBase from '@/AppMobileBase.vue'
import AppWebBase from '@/AppWebBase.vue'
import LoadersLogin from '@/components/generic/loaders/Login'
import ModalGeneric from '@/components/generic/modals/centered/Generic'
/* Assets */
import { nonAuthenticatedRoutes, initialRoutes } from '@/router/helpers/validations'
import { navigateByPath } from '@/router/helpers/navigation'
import { APP_VERSION } from '@/assets/constants/core'
import { isMobileValidator } from '@/assets/scripts/validators'
import { encryptData } from '@/assets/scripts/strings'
import '@/plugins/postlogin_loader.js'

/* Vue */
import { useRoute } from 'vue-router'
import { computed, watch, ref, onMounted } from 'vue'
import { getItemFromSessionStorage, removeItemFromSessionStorage } from '@/assets/scripts/windowStorage'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Firebase */
import { initializeApp } from 'firebase/app'
import { getAnalytics } from 'firebase/analytics'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { updateIsMobileApp, updateDeviceInUse, updateKeyboardHeight, updateAppThemeClientType, updateAppThemeMode } = mapActions()
const { menuFetch, submenuFetch } = mapActions(moduleMapper.MENU)
const { getMobileAppState, getDeviceInUse, getApplyTransparencyToApp, getClientType } = mapGetters()
const { forceLogout, updateSessionExpireState } = mapActions(moduleMapper.SESSION)
const { getAllCustomerInfo, getToken, getAppTheme, getAppThemeMode, getAccountGroups, getStatusBarStyle } = mapGetters(moduleMapper.SESSION)
const { paymentsOperatorsFetch } = mapActions(moduleMapper.PAYMENTS_SERVICES)
const currentRoute = useRoute()

const nonAuthenticatedArea = computed(() => {
	return nonAuthenticatedRoutes().has(currentRoute.path) || initialRoutes().has(currentRoute.path)
})

const applyLighterBackground = computed(() => {
	return currentRoute.path === '/home'
})

const notDesignShowroom = computed(() => {
	return !currentRoute.path.includes('designShowroom')
})

const authorizeSession = computed(() => {
	return currentRoute.path.includes('authorizeSession')
})

watch(() => getStatusBarStyle.value, () => {
	if (getMobileAppState.value && getDeviceInUse.value.toLowerCase() !== 'desktop') {
		getStatusBarStyle.value === 'lightContent' ? window.StatusBar.styleLightContent() : window.StatusBar.styleDefault()
	}
})

watch(() => getAppThemeMode.value, () => {
	if (getMobileAppState.value && getDeviceInUse.value.toLowerCase() === 'ios') {
		getAppThemeMode.value === 'light' ? window.Keyboard.setKeyboardStyle('light') : window.Keyboard.setKeyboardStyle('dark')
	}
})

const showApp = ref(false)
const isInMobile = isMobileValidator()
const showLoginSpinner = ref(false)
const reRenderSvgFlag = ref(false)
const triggerLoginSimpleExit = ref(false)
const applyLoginLoaderExitAnimation = ref(false)
const showRootedWarningModal = ref(false)

const updateLoginLoaderHandler = $event => {
	$event ? showLoginSpinner.value = true : triggerLoginSimpleExit.value = true
}

/* Initial requests for App to work */
const initalAppRequests = async () => {
    if (!getToken.value) {
		showApp.value = true
		updateAppThemeMode('system')
        updateSessionExpireState(false)

		return
	}

	updateAppThemeClientType(getAccountGroups.value.find(obj => obj.default).type.toLowerCase())

	let mockPayload
	if (process.env.VUE_APP_MOCKS === 'true') {
		mockPayload = { payload: getAccountGroups.value.find(obj => obj.default).type }
	}

	try {
		await menuFetch(mockPayload)
		if (getMobileAppState.value) await submenuFetch()
        if (window.cordova && window.cordova.platformId === 'android') window.StatusBar.overlaysWebView(true)
		nonBlockingInitialRequests()
	} catch (error) {
		forceLogout()
	}
}

const nonBlockingInitialRequests = async () => {
	try {
		await paymentsOperatorsFetch()
	} finally {
		showApp.value = true
	}
}

const setupClarityForWeb = () => {
	if (!process.env.VUE_APP_CLARITY || window.cordova) return
	const script = document.createElement('script')
	script.type = 'text/javascript'
	script.innerHTML = process.env.VUE_APP_CLARITY
	document.body.appendChild(script)

    if (process.env.VUE_APP_ENV === 'DEV' || process.env.VUE_APP_ENV === 'PRD') {
        if (!getToken.value) window.clarity('identify', 'anonymous', 'anonymoussession')
        else window.clarity('identify', encryptData(getAllCustomerInfo.value.acessionNumber), encryptData(getToken.value))
    }
}

/**********************************************************************
*                                                                     *
*                         	  onDeviceReady                           *
*                                                                     *
**********************************************************************/
const isTextInput = node => {
    return ['INPUT'].indexOf(node.nodeName) !== -1
}

const resizeFunction = () => {
    if (document.documentElement.clientHeight !== initialHeight.value) {
        const heightDiference = document.documentElement.clientHeight - initialHeight.value
        document.documentElement.style.setProperty('--android-top-padding', `${(heightDiference > 39 ? 28 : (heightDiference - 8))}px`)
    }
    window.removeEventListener('resize', resizeFunction)
}

const initialHeight = ref(document.documentElement.clientHeight)
const touchTime = ref(null)

const onDeviceReady = () => {
	window.cordova.plugin.http.setDataSerializer('json')
	window.screen.orientation.lock('portrait')
	window.cordova.plugin.http.setRequestTimeout(120.0)
	/* Status Bar plugin logic */
	/******************************************************************
	*                         	iOS only logic                     	  *
	******************************************************************/
	if (window.cordova.platformId === 'ios') {
		window.fullMobileHeight = window.innerHeight
        document.addEventListener('touchstart', e => {
            touchTime.value = new Date()
        }, false)

        document.addEventListener('touchend', e => {
            const diff = new Date() - touchTime.value
            if (diff < 150 && !isTextInput(e.target) && isTextInput(document.activeElement)) {
                document.activeElement.blur()
            }
        }, false)
	/* Android only logic */
	/******************************************************************
	*                         Android only logic                      *
	******************************************************************/
	} else {
        window.addEventListener('resize', resizeFunction)

        let tryCounter = 4
        const interval = setInterval(() => {
            if (tryCounter-- === 0) {
                clearInterval(interval)
                return
            }

            if (window.StatusBar && window.StatusBar.overlaysWebView) {
                // eslint-disable-next-line no-undef
                StatusBar.overlaysWebView(true)
                clearInterval(interval)
            }
        }, 100)

		/*
		* Logic to handle keyboard being shown,
		* This will apply padding bottom making our app content smaller, and all elements visible will be pushed up
		*/
		// Keyboard is about to be shown.
		window.addEventListener('keyboardWillShow', (event) => { updateKeyboardHeight(`${event.keyboardHeight}px`) })
		/*
		* Event for when keyboard is about to be closed.
		* I know this is not a valid padding, this way browser will apply the default value
		*/
		window.addEventListener('keyboardWillHide', () => { updateKeyboardHeight(null) })
	}
	document.addEventListener('backbutton', onBackKeyDown, false)

    // if ((process.env.VUE_APP_ENV === 'DEV' || process.env.VUE_APP_ENV === 'PRD') && window.cordova.platformId !== 'ios') {
	// 	const clarityConfig = { logLevel: window.ClarityPlugin.LogLevel.None, allowMeteredNetworkUsage: true }
    //     window.ClarityPlugin.initialize('ldhcgwosq1', () => {}, () => {}, clarityConfig)
    //     if (!getToken.value) {
    //         window.ClarityPlugin.setCustomUserId('anonymous', () => {}, () => {})
    //         window.ClarityPlugin.setCustomSessionId('anonymoussession', () => {}, () => {})
    //     } else {
    //         window.ClarityPlugin.setCustomUserId(encryptData(getAllCustomerInfo.value.acessionNumber), () => {}, () => {})
    //         window.ClarityPlugin.setCustomSessionId(encryptData(getToken.value), () => {}, () => {})
    //     }
    // }

    // if (process.env.VUE_APP_ENV === 'DEV_MDW') {
    //     const randomBytes = new Uint8Array(294) // emulating a public key
    //     crypto.getRandomValues(randomBytes)
    //     let randomBinaryString = ''
    //     randomBytes.forEach((v) => { randomBinaryString += String.fromCharCode(v) })
    //     // eslint-disable-next-line no-undef
    //     // const nonce = CryptoJS.SHA1(randomBinaryString)
    //     const nonce = crypto.subtle.digest('SHA-1', randomBinaryString)
    //     // eslint-disable-next-line no-undef
    //     integrity.check(btoa(nonce), function (message) {
    //         // success~
    //         alert(message)
    //     }, function (message) {
    //         // error
    //         alert(message + 'error')
    //     })
    // }

    const modalTitle = ref('')
    window.IRoot.isRooted(event => {
        modalTitle.value = window.cordova.platformId === 'ios' ? t('rootedWarningModal.iosTitle') : t('rootedWarningModal.androidTitle')

        showRootedWarningModal.value = event
    }, err => {
        console.log(err)
    })
}

const onBackKeyDown = () => {
	/* We are catching this to prevent default behaviour of mobile */
}

if (window.cordova) {
	/* To trigger mobile needed validations/logic */
	document.addEventListener('deviceready', onDeviceReady, false)
	updateIsMobileApp(window.cordova.platformId === 'android' || window.cordova.platformId === 'ios')
	updateDeviceInUse(window.cordova.platformId)

	if (window.cordova.platformId === 'ios') {
		document.querySelector('html').style.height = '100vh'
	}
}

/* In mobile we only want to trigger this after se setup our httpRequests */
initalAppRequests()
setupClarityForWeb()

/* Log of current version for testers */
console.log(`App version v4 ${APP_VERSION}`)

/* If redirect link after refresh we will send our user to that place */
if (getItemFromSessionStorage('bai-v4-aux-redirect-link-after-refresh')) {
	navigateByPath(getItemFromSessionStorage('bai-v4-aux-redirect-link-after-refresh'))
	setTimeout(() => {
		removeItemFromSessionStorage('bai-v4-aux-redirect-link-after-refresh')
	})
}

onMounted(() => {
	if (process.env.VUE_APP_FIREBASE && (!window.cordova)) {
        let firebaseConfig
        if (getClientType.value === 'particular') {
            firebaseConfig = {
                apiKey: 'AIzaSyAq3sFFGmxY02DdYHm31meOXHQVjAvwffY',
                authDomain: 'baidirecto-v4.firebaseapp.com',
                projectId: 'baidirecto-v4',
                storageBucket: 'baidirecto-v4.appspot.com',
                messagingSenderId: '650011752637',
                appId: '1:650011752637:web:62843c5dccd04888809fd4',
                measurementId: 'G-GKLFMR2WGW'
            }
        } else {
            firebaseConfig = {
                apiKey: 'AIzaSyAq3sFFGmxY02DdYHm31meOXHQVjAvwffY',
                authDomain: 'baidirecto-v4.firebaseapp.com',
                projectId: 'baidirecto-v4',
                storageBucket: 'baidirecto-v4.appspot.com',
                messagingSenderId: '650011752637',
                appId: '1:650011752637:web:df22683fdf3b3485809fd4',
                measurementId: 'G-CMCJJGV10N'
            }
        }

		// Initialize Firebase
		const app = initializeApp(firebaseConfig)
		const analytics = getAnalytics(app)
	}
})
</script>

<style lang="scss">
@import 'src/styles/main.scss';

#app, #app-content {
	width: 100%;
	height: 100%;
	position: relative;

	overflow: hidden;

	.status-bar-background-layer {
		width: 100%;
		position: absolute;
		top: 0;

		z-index: 10;
		background-color: var(--primary);

		&.mobile-status-bar-android {
			height: var(--android-top-padding); /*Android status bar*/
		}

		&.mobile-status-bar-ios {
			height: constant(safe-area-inset-top); /* iOS 11.0 */
			height: env(safe-area-inset-top); /* iOS 11.2 */
		}
	}

	&.apply-transparency {
		background-color: transparent;

		>:not(.dont-apply-transparency) {
			opacity: 0;
			pointer-events: none;
		}
	}

    &.mobile-app {
        -webkit-user-select: none; /* Safari */
        -ms-user-select: none; /* IE 10 and IE 11 */
        user-select: none; /* Standard syntax */
    }
}

#app-content {
    background-color: var(--secondary-light-4);
}
</style>
